import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Shield,
  ShieldCheck,
  ShieldOff,
  Smartphone,
  Key,
  Copy,
  Check,
  AlertTriangle,
  RefreshCw,
  QrCode,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiV1 } from '@/api/client';

export default function TwoFactorSettings() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [backupCodesDialogOpen, setBackupCodesDialogOpen] = useState(false);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  
  // Setup state
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  
  // Disable state
  const [disableCode, setDisableCode] = useState('');
  const [disabling, setDisabling] = useState(false);
  
  // Backup codes state
  const [backupCodes, setBackupCodes] = useState([]);
  const [regenerateCode, setRegenerateCode] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  
  const [copiedIndex, setCopiedIndex] = useState(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await apiV1.get('/auth/2fa/status');
      setStatus(response.data.data);
    } catch (error) {
      console.error('Error fetching 2FA status:', error);
      toast.error('Failed to load 2FA status');
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async () => {
    try {
      const response = await apiV1.post('/auth/2fa/setup');
      setSetupData(response.data.data);
      setBackupCodes(response.data.data.backup_codes);
      setSetupDialogOpen(true);
    } catch (error) {
      toast.error('Failed to start 2FA setup');
    }
  };

  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length < 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }
    
    setVerifying(true);
    try {
      await apiV1.post('/auth/2fa/verify', { code: verificationCode });
      toast.success('2FA has been enabled!');
      setSetupDialogOpen(false);
      setVerificationCode('');
      setSetupData(null);
      setBackupCodesDialogOpen(true); // Show backup codes one more time
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleDisable = async () => {
    if (!disableCode || disableCode.length < 6) {
      toast.error('Please enter your current 2FA code');
      return;
    }
    
    setDisabling(true);
    try {
      await apiV1.post('/auth/2fa/disable', { code: disableCode });
      toast.success('2FA has been disabled');
      setDisableDialogOpen(false);
      setDisableCode('');
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setDisabling(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    if (!regenerateCode || regenerateCode.length < 6) {
      toast.error('Please enter your current 2FA code');
      return;
    }
    
    setRegenerating(true);
    try {
      const response = await apiV1.post('/auth/2fa/backup-codes/regenerate', { code: regenerateCode });
      setBackupCodes(response.data.data);
      setRegenerateDialogOpen(false);
      setRegenerateCode('');
      setBackupCodesDialogOpen(true);
      toast.success('New backup codes generated');
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setRegenerating(false);
    }
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const copyAllCodes = () => {
    const codesText = backupCodes.join('\n');
    navigator.clipboard.writeText(codesText);
    toast.success('All backup codes copied to clipboard');
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Two-Factor Authentication
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-10 bg-muted rounded w-40"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card data-testid="two-factor-settings">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {status?.enabled ? (
                <ShieldCheck className="h-5 w-5 text-green-500" />
              ) : (
                <ShieldOff className="h-5 w-5 text-muted-foreground" />
              )}
              <CardTitle>Two-Factor Authentication</CardTitle>
              {status?.enabled && (
                <Badge variant="default" className="ml-2">Enabled</Badge>
              )}
            </div>
          </div>
          <CardDescription>
            Add an extra layer of security to your account using Google Authenticator or any TOTP-compatible app.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {status?.enabled ? (
            <>
              <Alert>
                <ShieldCheck className="h-4 w-4" />
                <AlertTitle>2FA is Active</AlertTitle>
                <AlertDescription>
                  Your account is protected with two-factor authentication.
                  {status.backup_codes_remaining > 0 && (
                    <span className="block mt-1 text-sm">
                      You have <strong>{status.backup_codes_remaining}</strong> backup codes remaining.
                    </span>
                  )}
                  {status.backup_codes_remaining <= 2 && status.backup_codes_remaining > 0 && (
                    <span className="block mt-1 text-amber-600 text-sm">
                      ⚠️ Consider generating new backup codes soon.
                    </span>
                  )}
                </AlertDescription>
              </Alert>
              
              <Separator />
              
              <div className="flex flex-wrap gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setRegenerateDialogOpen(true)}
                  data-testid="regenerate-backup-codes-btn"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Regenerate Backup Codes
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={() => setDisableDialogOpen(true)}
                  data-testid="disable-2fa-btn"
                >
                  <ShieldOff className="h-4 w-4 mr-2" />
                  Disable 2FA
                </Button>
              </div>
            </>
          ) : (
            <>
              <Alert variant="warning">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>2FA Not Enabled</AlertTitle>
                <AlertDescription>
                  Your account is not protected with two-factor authentication. 
                  We strongly recommend enabling it for additional security.
                </AlertDescription>
              </Alert>
              
              <Button onClick={handleSetup} data-testid="enable-2fa-btn">
                <Smartphone className="h-4 w-4 mr-2" />
                Enable Two-Factor Authentication
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Setup Dialog */}
      <Dialog open={setupDialogOpen} onOpenChange={setSetupDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              Set Up Two-Factor Authentication
            </DialogTitle>
            <DialogDescription>
              Scan the QR code with Google Authenticator, Authy, or any TOTP app.
            </DialogDescription>
          </DialogHeader>
          
          {setupData && (
            <div className="space-y-4">
              {/* QR Code */}
              <div className="flex justify-center p-4 bg-white rounded-lg">
                <img 
                  src={setupData.qr_code_png} 
                  alt="2FA QR Code" 
                  className="w-48 h-48"
                  data-testid="2fa-qr-code"
                />
              </div>
              
              {/* Manual Entry */}
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">
                  Or enter this code manually:
                </Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-2 bg-muted rounded text-sm font-mono">
                    {setupData.secret}
                  </code>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => {
                      navigator.clipboard.writeText(setupData.secret);
                      toast.success('Secret copied');
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {/* Verification */}
              <div className="space-y-2">
                <Label htmlFor="verify-code">Enter the 6-digit code from your app:</Label>
                <div className="flex gap-2">
                  <Input
                    id="verify-code"
                    type="text"
                    placeholder="000000"
                    maxLength={6}
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                    className="font-mono text-center text-lg tracking-widest"
                    data-testid="2fa-verification-input"
                  />
                  <Button 
                    onClick={handleVerify} 
                    disabled={verifying || verificationCode.length < 6}
                    data-testid="verify-2fa-btn"
                  >
                    {verifying ? 'Verifying...' : 'Verify'}
                  </Button>
                </div>
              </div>
              
              {/* Backup Codes Warning */}
              <Alert variant="warning">
                <Key className="h-4 w-4" />
                <AlertTitle>Save Your Backup Codes!</AlertTitle>
                <AlertDescription>
                  After setup, you'll receive backup codes. Save them securely - they're your only way to access your account if you lose your phone.
                </AlertDescription>
              </Alert>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Disable Dialog */}
      <Dialog open={disableDialogOpen} onOpenChange={setDisableDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Enter your current 2FA code to disable two-factor authentication.
              Your account will be less secure without it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="disable-code">Current 2FA Code</Label>
              <Input
                id="disable-code"
                type="text"
                placeholder="000000"
                maxLength={6}
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
                className="font-mono text-center text-lg tracking-widest"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisableDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDisable}
              disabled={disabling || disableCode.length < 6}
            >
              {disabling ? 'Disabling...' : 'Disable 2FA'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regenerate Backup Codes Dialog */}
      <Dialog open={regenerateDialogOpen} onOpenChange={setRegenerateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Regenerate Backup Codes</DialogTitle>
            <DialogDescription>
              Enter your current 2FA code to generate new backup codes.
              This will invalidate all existing backup codes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="regenerate-code">Current 2FA Code</Label>
              <Input
                id="regenerate-code"
                type="text"
                placeholder="000000"
                maxLength={6}
                value={regenerateCode}
                onChange={(e) => setRegenerateCode(e.target.value.replace(/\D/g, ''))}
                className="font-mono text-center text-lg tracking-widest"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegenerateDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRegenerateBackupCodes}
              disabled={regenerating || regenerateCode.length < 6}
            >
              {regenerating ? 'Generating...' : 'Generate New Codes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backup Codes Dialog */}
      <Dialog open={backupCodesDialogOpen} onOpenChange={setBackupCodesDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Your Backup Codes
            </DialogTitle>
            <DialogDescription>
              Save these codes securely. Each code can only be used once.
            </DialogDescription>
          </DialogHeader>
          
          <Alert variant="destructive" className="my-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Important!</AlertTitle>
            <AlertDescription>
              These codes will NOT be shown again. Save them now in a secure location.
            </AlertDescription>
          </Alert>
          
          <div className="grid grid-cols-2 gap-2">
            {backupCodes.map((code, index) => (
              <div 
                key={index}
                className="flex items-center justify-between p-2 bg-muted rounded font-mono text-sm"
              >
                <span>{code}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => copyToClipboard(code, index)}
                >
                  {copiedIndex === index ? (
                    <Check className="h-3 w-3 text-green-500" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </Button>
              </div>
            ))}
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={copyAllCodes}>
              <Copy className="h-4 w-4 mr-2" />
              Copy All
            </Button>
            <Button onClick={() => setBackupCodesDialogOpen(false)}>
              I've Saved My Codes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
